import { useCallback, useRef, useState } from "react";

interface DropzoneProps {
  disabled?: boolean;
  className?: string;
  onFiles: (files: File[]) => void;
}

export function Dropzone({ disabled, className, onFiles }: DropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) onFiles(files);
    },
    [disabled, onFiles]
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={[
        "flex flex-col items-center justify-center rounded-card border-2 border-dashed p-10 text-center transition-all",
        disabled
          ? "cursor-not-allowed border-surface-secondary bg-surface-secondary text-text-secondary"
          : "cursor-pointer bg-white hover:shadow-card-hover",
        dragging ? "border-rausch bg-rausch-light" : "border-border-card",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <svg
        className="mb-3 h-10 w-10 text-text-secondary"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"
        />
      </svg>
      <p className="text-base font-medium text-near-black">
        {dragging ? "Drop resume to screen" : "Drag & drop a resume here"}
      </p>
      <p className="mt-1 text-sm text-text-secondary">
        or <span className="font-medium text-rausch underline">browse</span> — PDF or TXT,
        up to ~20 pages
      </p>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.txt,.md,application/pdf,text/plain"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onFiles(files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
